/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-20 14:40:03
 */
import dayjs from 'dayjs'
import React from 'react'
import { message, Checkbox, Divider } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import {
  XC_RECOGNITION_STATUS,
  RecognitionRecord
} from '../types/contextInterface'

// 使用 RecognitionRecord 作为基础类型，添加额外字段
type XCRecognitionRecord = RecognitionRecord & {
  isFixedData?: boolean
}
import ScanningOverlay from '../../components/ScanningOverlay'
import ColumnHeaderWithTag from '../components/ColumnHeaderWithTag'
import RecognizingAnimation from '../components/RecognizingAnimation'
import PhotoUploadButton from '../components/PhotoUploadButton'
import PhotoPreviewThumbnail from '../components/PhotoPreviewThumbnail'
import PhotoUploadPreview from '../components/PhotoUploadPreview'
import type { XCIconAction } from '../components/PhotoPreviewThumbnail'
import ContentFeedback from '../components/ContentFeedback'
import BatchPreviewGrid from './BatchPreviewGrid'

// 上传处理函数类型
interface UploadHandlers {
  onLocalUpload?: (file: File) => void
  beforeUpload?: (file: File) => boolean
  handleChange?: (info: { file: unknown }, record: XCRecognitionRecord) => void
  getUploadState?: (
    recordId: string
  ) => { uploading?: boolean; imageUrl?: string } | undefined
}

// 反馈点击回调参数类型
interface FeedbackClickParams {
  record: XCRecognitionRecord
  fieldName: string
}

/**
 * 根据selectedHazardSource动态获取字段值
 * @param {Object} record - 记录对象
 * @param {string} fieldName - 基础字段名
 * @returns {any} 字段值
 */
const getDynamicFieldValue = (
  record: XCRecognitionRecord | null,
  fieldName: string
): React.ReactNode => {
  if (!record) return null

  // 检查是否为水印模式：selectedHazardSource 为索引值，直接通过索引获取对应项
  const hazardSourceArray = record.hazardSourceList || []
  const selectedIndex = record.selectedHazardSource ?? 0
  const selectedHazardSourceItem = hazardSourceArray[selectedIndex]
  const isWatermarkMode =
    selectedHazardSourceItem && selectedHazardSourceItem.name === 'watermark'

  const contentFieldName = `${fieldName}Content`

  // 如果是水印模式，只使用Content字段，不回退到普通字段
  if (isWatermarkMode) {
    return record[contentFieldName] || null
  }

  // 否则使用普通字段
  return record[fieldName]
}

// 统一处理列的编辑状态和输入类型
const getColumnEditableConfig = (
  record: XCRecognitionRecord,
  defaultEditable = true,
  defaultInputType: string | undefined = undefined,
  allowEditWhenPendingFill = false
) => {
  // 如果是等待识别或识别中状态
  const isFixedData =
    record.isFixedData &&
    (record.status === XC_RECOGNITION_STATUS.WAITING ||
      record.status === XC_RECOGNITION_STATUS.RECOGNIZING)

  // 如果是固定数据且允许在"待填写"状态下编辑，则保持可编辑
  if (isFixedData && allowEditWhenPendingFill) {
    return {
      editable: defaultEditable,
      inputType: defaultInputType
    }
  }

  return {
    editable: isFixedData ? false : defaultEditable,
    inputType: defaultInputType
  }
}

// 渲染固定数据的通用函数
/**
 * 渲染固定数据内容的通用函数
 * @param {Object} record - 当前行的数据记录
 * @param {string} defaultValue - 默认显示值，通常为字段值或"待识别"
 * @param {boolean} showLoading - 是否显示加载动画，默认true
 * @param {boolean} showPendingFill - 是否显示待填充状态，默认false
 * @param {boolean} useGradientText - 是否使用渐变文字效果，默认false
 * @param {boolean} isSecondRow - 是否为第二行数据，默认false
 * @param {boolean} isShowingResults - 是否正在显示识别结果，默认false
 * @param {number} currentShowingIndex - 当前显示的字段索引，默认-1
 * @param {number} fieldIndex - 当前字段在识别序列中的索引，默认-1
 * @param {number} currentRowIndex - 当前行索引，默认-1
 * @param {number} animationRowIndex - 执行动画的行索引，默认-1
 * @returns {JSX.Element} 渲染的内容组件
 */
const renderFixedDataContent = (
  record: XCRecognitionRecord,
  defaultValue: React.ReactNode,
  showLoading = true,
  showPendingFill = false,
  useGradientText = false,
  isSecondRow = false,
  isShowingResults = false,
  currentShowingIndex = -1,
  fieldIndex = -1,
  currentRowIndex = -1,
  animationRowIndex = -1
): React.ReactNode => {
  // 批量模式下统一显示“批量识别中”文案
  const isBatchModeRow =
    Array.isArray(record.selectedPreviewImages) &&
    record.selectedPreviewImages.length > 1
  const recognizingText = isBatchModeRow ? '批量识别中' : '识别中'

  // 检查是否应该在当前行显示动画（只在指定的行索引执行动画）
  const shouldShowAnimation =
    record.isAnimationRow && currentRowIndex === animationRowIndex

  if (shouldShowAnimation) {
    // 识别中状态或已完成状态且需要显示loading的列
    if (
      (record.status === XC_RECOGNITION_STATUS.RECOGNIZING ||
        record.status === XC_RECOGNITION_STATUS.COMPLETED) &&
      showLoading &&
      !isShowingResults
    ) {
      return (
        <RecognizingAnimation
          text={recognizingText}
          style={{ minHeight: '32px', padding: '4px 8px' }}
        />
      )
    }
    // 逐步显示结果时，根据字段位置和当前显示进度决定显示状态
    if (isShowingResults && showLoading && fieldIndex >= 0) {
      // 如果当前字段已经显示完成（索引小于当前显示索引），显示实际内容
      if (fieldIndex < currentShowingIndex) {
        const content = defaultValue || ''
        const formattedContent =
          typeof content === 'string' && content.includes('\n') ? (
            <div style={{ whiteSpace: 'pre-line' }}>{content}</div>
          ) : (
            content
          )
        return useGradientText && isSecondRow ? (
          <span className="gradient-text-content">{formattedContent}</span>
        ) : (
          formattedContent
        )
      }
      // 如果是当前正在显示的字段，显示"生成中"
      else if (fieldIndex === currentShowingIndex) {
        return (
          <RecognizingAnimation
            text="生成中"
            style={{ minHeight: '32px', padding: '4px 8px' }}
          />
        )
      }
      // 如果是还未显示的字段，显示"识别中"（批量时为“批量识别中”）
      else {
        return (
          <RecognizingAnimation
            text={recognizingText}
            style={{ minHeight: '32px', padding: '4px 8px' }}
          />
        )
      }
    }
    // 识别中和待识别状态且需要显示"待填写"的列
    if (
      (record.status === XC_RECOGNITION_STATUS.RECOGNIZING ||
        record.status === XC_RECOGNITION_STATUS.WAITING) &&
      showPendingFill
    ) {
      return ''
    }
    const content = defaultValue || '待识别'
    const formattedContent =
      typeof content === 'string' && content.includes('\n') ? (
        <div style={{ whiteSpace: 'pre-line' }}>{content}</div>
      ) : (
        content
      )
    return useGradientText && isSecondRow ? (
      <span className="gradient-text-content">{formattedContent}</span>
    ) : (
      formattedContent
    )
  }

  const formattedValue =
    typeof defaultValue === 'string' && defaultValue.includes('\n') ? (
      <div style={{ whiteSpace: 'pre-line' }}>{defaultValue}</div>
    ) : (
      defaultValue
    )
  const emptyValue = (
    <div
      style={{
        textAlign: 'center',
        color: '#999999'
      }}
    >
      未识别到，点击填写
    </div>
  )
  return useGradientText && isSecondRow ? (
    <span className="gradient-text-content">{formattedValue}</span>
  ) : (
    formattedValue || emptyValue
  )
}

/**
 * 获取基础列配置数据
 * @param {Function} onPhotoSelect - 照片选择回调函数
 * @param {Function} onPhotoView - 照片预览回调函数
 * @param {boolean} isShowingResults - 是否显示结果
 * @param {number} currentShowingIndex - 当前显示索引
 * @param {Object} uploadHandlers - 上传处理函数对象
 * @param {Function} uploadHandlers.beforeUpload - 上传前验证回调函数
 * @param {Function} uploadHandlers.handleChange - 上传状态变化回调函数
 * @param {Function} uploadHandlers.getUploadState - 获取上传状态的函数
 * @param {Function} onSelect - 选择记录回调函数
 * @param {Function} onFeedbackClick - 反馈点击回调函数

 * @returns {Array} 列配置数组
 */
export const getBaseColumnsData = (
  onPhotoSelect: (() => void) | null = null,
  onPhotoView:
    | ((record: XCRecognitionRecord, supportBbox: boolean) => void)
    | null = null,
  isShowingResults = false,
  currentShowingIndex = -1,
  uploadHandlers: UploadHandlers = {},
  onSelect: ((record: XCRecognitionRecord) => void) | null = null,
  onFeedbackClick: ((params: FeedbackClickParams) => void) | null = null,
  animationRowIndex = -1,
  allSelected = false,
  someSelected = false,
  handleSelectAllRecognitionRecords: (e: CheckboxChangeEvent) => void = () => {}
) => [
  {
    title: (
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={handleSelectAllRecognitionRecords}
      />
    ),
    dataIndex: 'operation',
    width: 65,
    align: 'center' as const,
    fixed: 'left' as const,
    render: (_text: unknown, record: XCRecognitionRecord) => {
      // 如果record不存在，返回空内容
      if (!record) {
        return null
      }

      // 对于固定行不显示删除按钮
      if (record.isAnimationRow) {
        return null
      }

      return (
        <div>
          <Checkbox
            className="blue-border-checkbox"
            checked={record.isSelected}
            onChange={() => {
              if (onSelect) {
                onSelect(record)
              }
            }}
          />
        </div>
      )
    }
  },
  {
    title: '隐患照片',
    dataIndex: 'hazardPhoto',
    width: 235,
    align: 'center' as const,
    fixed: 'left' as const,
    render: (_text: unknown, record: XCRecognitionRecord, _index: number) => {
      // 如果是固定数据，根据状态显示不同内容
      if (record.isAnimationRow) {
        if (record.status === 'waiting') {
          return (
            <div style={{ justifyContent: 'center', alignItems: 'center' }}>
              <PhotoUploadButton
                onPhotoSelect={onPhotoSelect ?? undefined}
                onLocalUpload={uploadHandlers.onLocalUpload}
                beforeUpload={
                  uploadHandlers.beforeUpload as (() => boolean) | undefined
                }
              />
            </div>
          )
        } else if (record.status === 'recognizing') {
          const images =
            Array.isArray(record.selectedPreviewImages) &&
            record.selectedPreviewImages.length > 0
              ? record.selectedPreviewImages
              : record.url
                ? [record.url]
                : []
          const isMulti = images.length > 1

          if (isMulti) {
            return (
              <BatchPreviewGrid
                images={images}
                tip={record.batchModeTip || '批量识别中'}
                width={200}
                height={200}
              />
            )
          }

          return (
            <div
              style={{
                width: 200,
                height: 200,
                position: 'relative',
                backgroundImage: record.url ? `url(${record.url})` : 'none',
                backgroundColor: record.url ? 'transparent' : '#f5f5f5',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              {!record.url && (
                <div style={{ color: '#999', fontSize: '12px' }}>识别中...</div>
              )}
              {record.url && (
                <ScanningOverlay
                  visible={true}
                  width={200}
                  height={200}
                  laserColor="#0093FF"
                  duration={2}
                  direction="vertical"
                />
              )}
            </div>
          )
        }
      }

      // 正常数据的渲染逻辑
      if (record.url) {
        return (
          <PhotoPreviewThumbnail
            imageUrl={record.url}
            width={200}
            height={200}
            bbox={record.bbox}
            onClick={() => {
              // 调用照片预览回调 - 隐患照片支持框选
              onPhotoView && onPhotoView(record, true)
            }}
          />
        )
      }
    }
  },

  {
    title: '拍摄人',
    dataIndex: 'nickName',
    width: 100,
    align: 'center' as const,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      return renderFixedDataContent(
        record,
        record.nickName || ' ',
        false,
        false,
        false,
        index === 1,
        false,
        -1,
        -1,
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="项目名称"
        content="AI智能识别的建筑项目名称信息"
        width={215}
      />
    ),
    dataIndex: 'projectName',
    width: 215,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    required: true,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      return renderFixedDataContent(
        record,
        record.projectName || '',
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        0, // fieldIndex for projectName
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="单体名称"
        content="AI智能识别的建筑单体名称信息"
        width={215}
      />
    ),
    dataIndex: 'monomerName',
    width: 215,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    required: true,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      const monomerName = getDynamicFieldValue(record, 'monomerName')
      return renderFixedDataContent(
        record,
        monomerName,
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        1, // fieldIndex for monomerName
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="整改责任人"
        content=""
        isAI={false}
        width={150}
      />
    ),
    dataIndex: 'RectificationManager',
    width: 150,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, true).editable,
    required: true,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      return renderFixedDataContent(
        record,
        record.RectificationManager || ' ',
        false,
        true,
        false,
        index === 1,
        false,
        -1,
        -1,
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="存在的隐患"
        content="AI智能识别的安全隐患问题描述"
        width={315}
      />
    ),
    dataIndex: 'potentialHazardList',
    width: 315,
    align: 'left' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    supportAgain: (record: XCRecognitionRecord) =>
      record.status === XC_RECOGNITION_STATUS.COMPLETED,
    showHazardSourceSelector: true,
    feedbackComponent: (record: XCRecognitionRecord) => {
      return (
        <ContentFeedback
          visible={record.status === XC_RECOGNITION_STATUS.COMPLETED}
          fieldName="存在的隐患"
          onFeedbackClick={() => {
            onFeedbackClick?.({
              record,
              fieldName: '存在的隐患'
            })
          }}
        />
      )
    },
    showAgainGenerate: (record: XCRecognitionRecord) => {
      return record.status === XC_RECOGNITION_STATUS.COMPLETED
    },
    regenerateTip: (record: XCRecognitionRecord) => {
      if (
        record.status === XC_RECOGNITION_STATUS.COMPLETED &&
        record.standardList
      ) {
        // 获取拼接后的文字内容
        const standardList = getDynamicFieldValue(record, 'standardList') || []
        const concatenatedText = Array.isArray(standardList)
          ? standardList.join('\n')
          : ''

        // 如果拼接成的文字大于100字，不展示提示
        if (concatenatedText.length > 60) {
          return null
        }

        return '点击开始编辑，重新生成相关依据、整改措施等相关项'
      }
      return null
    },
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      const hazardList =
        getDynamicFieldValue(record, 'potentialHazardList') || []

      // 根据状态决定defaultValue
      let defaultValue
      if (
        record.status === XC_RECOGNITION_STATUS.WAITING ||
        record.status === XC_RECOGNITION_STATUS.RECOGNIZING
      ) {
        // 等待或识别中状态显示"待识别"
        defaultValue =
          Array.isArray(hazardList) && hazardList.length > 0
            ? hazardList.join('\n')
            : '待识别'
      } else {
        // 识别完成状态，如果没有数据则显示空字符串
        defaultValue =
          Array.isArray(hazardList) && hazardList.length > 0
            ? hazardList.join('\n')
            : ''
      }

      const content = renderFixedDataContent(
        record,
        defaultValue,
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        2, // fieldIndex for potentialHazardList
        index,
        animationRowIndex
      )
      return content
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="相关依据"
        content="AI智能识别的相关法规标准依据"
        width={315}
      />
    ),
    dataIndex: 'standardList',
    width: 315,
    align: 'left' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    // feedbackComponent: (record, index) => {
    //   return (
    //     <ContentFeedback
    //       record={record}
    //       visible={record.status === XC_RECOGNITION_STATUS.COMPLETED}
    //       fieldName="相关依据"
    //       index={index}
    //       colIndex={2}
    //       onFeedbackClick={onFeedbackClick}
    //     />
    //   )
    // },
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      const standardList = getDynamicFieldValue(record, 'standardList') || []

      // 根据状态决定defaultValue
      let defaultValue
      if (
        record.status === XC_RECOGNITION_STATUS.WAITING ||
        record.status === XC_RECOGNITION_STATUS.RECOGNIZING
      ) {
        // 等待或识别中状态显示"待识别"
        defaultValue =
          Array.isArray(standardList) && standardList.length > 0
            ? standardList.join('\n')
            : '待识别'
      } else {
        // 识别完成状态，如果没有数据则显示空字符串
        defaultValue =
          Array.isArray(standardList) && standardList.length > 0
            ? standardList.join('\n')
            : ''
      }

      const content = renderFixedDataContent(
        record,
        defaultValue,
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        3, // fieldIndex for standardList
        index,
        animationRowIndex
      )
      return content
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="整改措施"
        content="AI智能推荐的整改处理措施"
        width={255}
      />
    ),
    dataIndex: 'RectificationMeasure',
    width: 255,
    align: 'left' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    // feedbackComponent: (record, index) => {
    //   return (
    //     <ContentFeedback
    //       record={record}
    //       visible={record.status === XC_RECOGNITION_STATUS.COMPLETED}
    //       fieldName="相关依据"
    //       index={index}
    //       colIndex={2}
    //       onFeedbackClick={onFeedbackClick}
    //     />
    //   )
    // },
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      const rectificationMeasure = getDynamicFieldValue(
        record,
        'RectificationMeasure'
      )
      const content = renderFixedDataContent(
        record,
        rectificationMeasure,
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        4, // fieldIndex for RectificationMeasure
        index,
        animationRowIndex
      )
      return content
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="施工单位"
        content=""
        isAI={true}
        width={210}
      />
    ),
    dataIndex: 'ConstructioUnit',
    width: 210,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, true).editable,
    required: true,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 根据状态决定defaultValue
      let defaultValue
      if (
        record.status === XC_RECOGNITION_STATUS.WAITING ||
        record.status === XC_RECOGNITION_STATUS.RECOGNIZING
      ) {
        // 等待或识别中状态显示"待识别"
        defaultValue = record.ConstructioUnit || '待识别'
      } else {
        // 识别完成状态，如果没有数据则显示空字符串
        defaultValue = record.ConstructioUnit || ''
      }

      return renderFixedDataContent(
        record,
        defaultValue,
        true, // showLoading = true 以支持识别动画
        true, // showPendingFill = true
        false, // useGradientText = false
        false, // isSecondRow = false
        isShowingResults,
        currentShowingIndex,
        5, // fieldIndex for ConstructioUnit
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="问题类型"
        content="AI智能分类的问题类型标签"
        width={155}
      />
    ),
    dataIndex: 'questionType',
    width: 155,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, false).editable,
    required: true,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      // 动态获取字段值
      const questionType = getDynamicFieldValue(record, 'questionType')

      // 根据状态决定defaultValue
      let defaultValue
      if (
        record.status === XC_RECOGNITION_STATUS.WAITING ||
        record.status === XC_RECOGNITION_STATUS.RECOGNIZING
      ) {
        // 等待或识别中状态显示"待识别"
        defaultValue = questionType || '待识别'
      } else {
        // 识别完成状态，如果没有数据则显示空字符串
        defaultValue = questionType || ''
      }

      return renderFixedDataContent(
        record,
        defaultValue,
        true,
        false,
        true,
        index === animationRowIndex &&
          record.status !== XC_RECOGNITION_STATUS.WAITING,
        isShowingResults,
        currentShowingIndex,
        6, // fieldIndex for questionType
        index,
        animationRowIndex
      )
    }
  },
  {
    title: '整改工单编号',
    dataIndex: 'uuid',
    width: 190,
    align: 'center' as const,
    editable: false, // 不支持编辑
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      return renderFixedDataContent(
        record,
        record.uuid || ' ',
        false,
        false,
        false,
        index === 1
      )
    }
  },
  {
    title: '检查日期',
    dataIndex: 'createTime',
    width: 100,
    align: 'center' as const,
    render: (_text: unknown, record: XCRecognitionRecord, index: number) => {
      return renderFixedDataContent(
        record,
        record.createTime || ' ',
        false,
        false,
        false,
        index === 1,
        false,
        -1,
        -1,
        index,
        animationRowIndex
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="整改期限"
        content=""
        isAI={false}
        width={170}
      />
    ),
    dataIndex: 'deadline',
    width: 170,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, 'date', true).editable,
    inputType: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, 'date', true).inputType,
    // 添加日期验证规则
    datePickerProps: (record: XCRecognitionRecord) => ({
      disabledDate: (current) => {
        // 禁止选择检测日期之前的日期
        if (record.createTime) {
          const createDate = dayjs(record.createTime).startOf('day')
          return current && current < createDate
        }
        return false
      }
    }),
    render: (_text: unknown, record: XCRecognitionRecord) => {
      // 如果是固定数据，计算默认的整改期限（检测时间+3天）
      if (record.id === -1) {
        const defaultDeadline =
          record.createTime && record.createTime !== '待识别'
            ? dayjs(record.createTime).add(3, 'days').format('YYYY-MM-DD')
            : dayjs().add(3, 'days').format('YYYY-MM-DD')
        return renderFixedDataContent(record, defaultDeadline, false, true)
      }

      // 对于正常数据，如果有整改期限则格式化显示，否则显示空
      if (record.deadline) {
        return dayjs(record.deadline).isValid()
          ? dayjs(record.deadline).format('YYYY-MM-DD')
          : record.deadline
      }
      return ''
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="整改前照片"
        content=""
        isAI={false}
        width={170}
      />
    ),
    dataIndex: 'beforeCorrectionPhoto',
    width: 135,
    align: 'center' as const,
    render: (_text: unknown, record: XCRecognitionRecord) => {
      if (record.isAnimationRow && !record.url?.length) {
        return renderFixedDataContent(record, '待显示', false, true)
      }

      // 使用隐患照片的url作为整改前照片
      const imageUrl = record.url

      if (imageUrl) {
        return (
          <PhotoPreviewThumbnail
            imageUrl={imageUrl}
            width={102}
            height={102}
            onClick={() => {
              // 调用照片预览回调 - 整改前照片不支持框选
              onPhotoView && onPhotoView(record, false)
            }}
          />
        )
      }
      return '无照片'
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="整改后照片"
        content=""
        isAI={false}
        width={170}
      />
    ),
    dataIndex: 'afterCorrectionPhoto',
    width: 135,
    align: 'center' as const,
    render: (_text: unknown, record: XCRecognitionRecord) => {
      if (record.isAnimationRow) {
        return renderFixedDataContent(record, '待上传', false, true)
      }

      const { handleChange, getUploadState } = uploadHandlers as UploadHandlers
      const recordId = record.id?.toString() || 'temp'
      const uploadState = getUploadState?.(recordId)

      // 优先使用 uploadState 中的 imageUrl，其次使用 record 中的数据
      const imageUrl =
        uploadState?.imageUrl ||
        (record.photoFilesAfter && record.photoFilesAfter.length > 0
          ? record.photoFilesAfter[0]
          : undefined)

      // 自定义图标配置：有图片时显示替换按钮
      const customIconActions: XCIconAction[] = [
        { type: 'swap', icon: 'swap', position: 'bottom-right' }
      ]

      return (
        <PhotoUploadPreview
          imageUrl={imageUrl}
          width={102}
          height={102}
          uploading={uploadState?.uploading}
          onPreview={(url) => {
            console.log('整改后照片预览被触发，URL:', url)
            // 创建一个临时record对象，包含整改后照片的URL
            const tempRecord = {
              ...record,
              url: url // 使用整改后照片的URL
            }
            console.log('创建的临时record:', tempRecord)
            // 调用照片预览回调 - 整改后照片不支持框选
            if (onPhotoView) {
              console.log('调用onPhotoView函数')
              onPhotoView(tempRecord, false)
            } else {
              console.log('onPhotoView函数未定义')
            }
          }}
          onUpload={(fileObj) => {
            try {
              // fileObj 已经是符合 handleChange 期望的格式
              handleChange && handleChange({ file: fileObj }, record)
            } catch (error) {
              console.error('处理上传文件时出错:', error)
              message.error('上传处理失败，请重试')
            }
          }}
          customIconActions={customIconActions}
        />
      )
    }
  },
  {
    title: (
      <ColumnHeaderWithTag
        title="完成时间"
        content=""
        isAI={false}
        width={170}
      />
    ),
    dataIndex: 'finishTime',
    width: 170,
    align: 'center' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, 'date', true).editable,
    inputType: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, 'date', true).inputType,
    render: (_text: unknown, record: XCRecognitionRecord) => {
      // 如果是固定数据，直接使用 renderFixedDataContent 处理
      if (record.isAnimationRow) {
        return renderFixedDataContent(record, '', false, true)
      }

      // 对于正常数据，如果有完成时间则格式化显示，否则显示空
      if (record.finishTime) {
        return dayjs(record.finishTime).isValid()
          ? dayjs(record.finishTime).format('YYYY-MM-DD')
          : record.finishTime
      }
      return ''
    }
  },
  {
    title: (
      <ColumnHeaderWithTag title="备注" content="" isAI={false} width={320} />
    ),
    dataIndex: 'comment',
    width: 320,
    align: 'left' as const,
    editable: (record: XCRecognitionRecord) =>
      getColumnEditableConfig(record, true, undefined, true).editable,
    render: (_text: unknown, record: XCRecognitionRecord) => {
      const content = renderFixedDataContent(
        record,
        record.comment || ' ',
        false,
        true
      )
      return content
    }
  }
]

// waitingRecognitionInitialData 已移动到 ../constants/initialFixedData.ts 文件中

// 初始化基础列配置
export const getInitialBaseColumns = () => {
  const baseColumns = [
    'operation',
    'hazardPhoto',
    'uuid',
    'nickName',
    'projectName',
    'monomerName',
    'RectificationManager',
    'potentialHazardList',
    'standardList',
    'thinking',
    'RectificationMeasure',
    'ConstructioUnit',
    'questionType',
    'createTime',
    'deadline',
    'beforeCorrectionPhoto',
    'afterCorrectionPhoto',
    'finishTime',
    'comment'
  ]

  return baseColumns
}

/**
 * 根据列的索引获取对应的列名(title)和dataIndex
 * @param {number} index - 列的索引（第几列，从0开始）
 * @returns {Object} 包含title和dataIndex的对象，如果找不到则返回空对象
 */
export const getTabName = (index: number) => {
  // 动态获取列配置
  const columns = getBaseColumnsData()

  console.log('columns', columns, index)

  // 根据索引获取对应列的title和dataIndex
  if (index >= 0 && index < columns.length) {
    const column = columns[index]
    let title = column.title || ''

    // 如果title是React组件，提取其中的实际文本
    if (typeof title === 'object' && title.props && title.props.title) {
      title = title.props.title
    }

    return {
      title: title,
      dataIndex: column.dataIndex || ''
    }
  }

  return {
    title: '',
    dataIndex: ''
  }
}
